import { Grid, Pagination, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { Environment } from "../../../shared/environment";
import { useToast } from "../../../shared/hooks";
import { ISinisterValues, SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { globalTranslate } from "../../../shared/utils";

interface Props {
	BorderoId?: string;
	disableFetch?: boolean;
	rows?: ISinisterValues[];
}

export function TableDetailBordero({ BorderoId, disableFetch = false, rows = [] }: Props) {
	const [internalRows, setInternalRows] = useState<ISinisterValues[]>(rows);
	const [totalCount, setTotalCount] = useState(internalRows.length || 0);
	const [isLoading, setIsLoading] = useState(false);
	const [pagina, setPagina] = useState(1);
	const toastHandler = useToast();

	const handleFetchBorderos = async () => {
		try {
			setIsLoading(true);

			const result = await SinisterService.getValuesSinisterByBorderoId(BorderoId!);

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					messageOrContent: (
						<ToastContent title="Erro ao carregar valores do borderô" content={result.message || "Não foi possível buscar os dados."} />
					),
				});
				return;
			}

			const data = Array.isArray(result) ? result : [];

			setInternalRows(data);
			setTotalCount(result.total ?? data.length);
		} catch (error: any) {
			toastHandler.present({
				type: "error",
				messageOrContent: <ToastContent title="Erro inesperado" content={error?.message || "Algo deu errado ao carregar os dados."} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!disableFetch && BorderoId) {
			handleFetchBorderos();
		}
	}, [BorderoId, disableFetch]);

	useEffect(() => {
		if (disableFetch && rows?.length) {
			setInternalRows(rows);
			setTotalCount(rows.length);
		}
	}, [disableFetch, rows]);

	return (
		<>
			<LoadingComp isopen={isLoading} />

			<Grid container item spacing={2}>
				<Grid item xs={12}>
					<Table aria-label="Tabela de Detalhes do Borderô">
						<TableHead>
							<TableRow sx={{ background: "#f5fbfd" }}>
								<TableCell> Débito </TableCell>
								<TableCell> Referência</TableCell>
								<TableCell> Valor</TableCell>
								<TableCell> Aprovado</TableCell>
								<TableCell> Abertura</TableCell>
								<TableCell> Vencimento</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{internalRows?.map((row) => (
								<TableRow key={row.id} hover>
									<TableCell sx={{ fontWeight: "normal" }}>
										<Typography variant="subtitle2" color={"#5c5858"} sx={{ fontWeight: "normal" }}>
											{globalTranslate(row.typeId)}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="subtitle2" color={"#5c5858"} sx={{ fontWeight: "normal" }}>
											{row.reference}
										</Typography>
									</TableCell>

									<TableCell sx={{ color: "#959595", fontWeight: "normal" }}>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
											{parseFloat(`${row.value}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
										</Typography>
									</TableCell>

									<TableCell sx={{ color: "#959595", fontWeight: "normal" }}>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
											{parseFloat(`${row.valueApproved}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
										</Typography>
									</TableCell>

									<TableCell sx={{ color: "#959595", fontWeight: "normal" }}>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
											{row.createdAt && format(new Date(row.createdAt), "P", { locale: ptBR })}
										</Typography>
									</TableCell>

									<TableCell sx={{ color: "#959595", fontWeight: "normal" }}>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
											{row.dueDate && format(new Date(row.dueDate), "P", { locale: ptBR })}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>

						{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}

						<TableFooter>
							{totalCount > 0 && (
								<TableRow>
									<TableCell colSpan={4} sx={{ borderBottom: "none" }}>
										<Pagination
											page={pagina}
											count={Math.ceil(totalCount / Environment.LIMITE_LINHAS)}
											onChange={(_, newPage) => setPagina(newPage)}
										/>
									</TableCell>
									<TableCell colSpan={2} sx={{ borderBottom: "none" }}>
										<Typography variant="body1">
											Exibindo {pagina * Environment.LIMITE_LINHAS - (Environment.LIMITE_LINHAS - 1)} -{" "}
											{Environment.LIMITE_LINHAS * pagina < totalCount ? Environment.LIMITE_LINHAS * pagina : totalCount} de{" "}
											<Typography
												component="span"
												variant="overline"
												sx={{ fontWeight: "bold", color: "primary.main", fontSize: "16px" }}
											>
												{totalCount}
											</Typography>{" "}
											registro(s)
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableFooter>
					</Table>
				</Grid>
			</Grid>
		</>
	);
}
